// src/components/SimpleComponents/SimpleSelectCompany.jsx
// *******************************************************************************
// SIMPLE SELECT COMPANY
// *******************************************************************************
// Select component for choosing a company from the current subscription.
// Changes the API header (x-companyid). ListProvider detects the company change
// via its effect and automatically refreshes data preserving all query state.
// Only visible when page config has filterByCompany: true.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import React, { useState, useCallback, useMemo } from 'react';
import { Grid, Select, Tooltip, theme } from 'antd';
import { ShopOutlined } from '@ant-design/icons';
import useZustand from 'hooks/useZustand';
import translateX from 'utils/translateX';
import utils from 'utils/utils';

const { useBreakpoint } = Grid;

// Fixed widths to prevent flickering on re-render
const DESKTOP_WIDTH = 180;
const MOBILE_WIDTH = 150;

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimpleSelectCompany = ({ style = {}, disabled = false, fullWidth = false }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedCompanies = useZustand('auth', 'companies');
  const storedCompany = useZustand('auth', 'company');
  const setCompanyStore = useZustand('auth', 'setCompany');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = theme.useToken();
  const screens = useBreakpoint();

  // Memoize isMobile to prevent unnecessary recalculations
  const isMobile = useMemo(() => !utils.getBreakPoint(screens).includes('sm'), [screens]);

  // Memoize select style to prevent flickering on re-render
  const selectStyle = useMemo(
    () => ({
      width: fullWidth ? '100%' : 'auto',
      minWidth: 224,
      maxWidth: fullWidth ? '100%' : 224,
      ...style,
    }),
    [isMobile, style, fullWidth]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const placeholder = translateX('select_company');

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [open, setOpen] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleChange = useCallback(
    value => {
      setOpen(false);

      // Only proceed if company actually changed
      if (value === storedCompany?._id) return;

      // Update store (this also updates the API header x-companyid)
      // NOTE: ListProvider has an effect that watches storedCompany?._id and
      // automatically refreshes when it changes, preserving all query state
      // (search, sort, filter, pagination). No need to call refresh here.
      setCompanyStore(value);
    },
    [setCompanyStore, storedCompany?._id]
  );

  const handleDropdownVisibleChange = useCallback(visible => {
    setOpen(visible);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  // Memoize options to prevent recalculation
  const selectOptions = useMemo(
    () =>
      storedCompanies?.map(c => ({
        value: c._id,
        label: c.name || c.razaoSocial || c.fullName || '-',
        company: c,
      })) || [],
    [storedCompanies]
  );

  // Memoize label renderer to prevent recreation (selected value - UPPERCASE)
  const labelRenderer = useCallback(
    ({ label }) => (
      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <ShopOutlined style={{ color: token.colorPrimary }} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', textTransform: 'uppercase' }}>
          {label}
        </span>
      </span>
    ),
    []
  );

  // Memoize option renderer to prevent recreation (dropdown list - UPPERCASE)
  const optionRenderer = useCallback(
    option => (
      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <ShopOutlined style={{ color: token.colorPrimary }} />
        <span style={{ textTransform: 'uppercase' }}>{option.data.label}</span>
      </span>
    ),
    []
  );

  // Check if should be visible (more than 1 company)
  const shouldRender = storedCompanies && storedCompanies.length > 1;

  // Return null if not enough companies
  if (!shouldRender) {
    return null;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Tooltip title={translateX('selected_company')} placement="bottom">
      <Select
        showSearch={{ optionFilterProp: 'label' }}
        placeholder={placeholder}
        disabled={disabled}
        open={open}
        onOpenChange={handleDropdownVisibleChange}
        style={selectStyle}
        value={storedCompany?._id}
        options={selectOptions}
        onChange={handleChange}
        labelRender={labelRenderer}
        optionRender={optionRenderer}
      />
    </Tooltip>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default React.memo(SimpleSelectCompany);

// *******************************************************************************
// HELPERS
// *******************************************************************************
